import { CommentShowAction } from '@src/js/constant';
import type { ICommentManager } from '@src/slide/application/service/comment-manager/core/pc/comment-manager.interface';
import type { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import type { ICTPoint2DDelta } from '@tencent/slide-sdk-model';
import React, { CSSProperties, useState } from 'react';
import ActiveCommentLessThan100Icon from '../svg/active-comment-2.svg'; // 小于三位数
import ActiveCommentLargerThan100Icon from '../svg/active-comment-3.svg'; // 大于等于三位数
import CommentLessThan100Icon from '../svg/inactive-comment-2.svg'; // 小于三位数
import CommentLargerThan100Icon from '../svg/inactive-comment-3.svg'; // 大于等于三位数
import { EventCodeType, TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import Style from './style.less';

const ANCHOR_WIDTH = 24;
const ANCHOR_HEIGHT = 24;

interface ICommentAnchorProps {
    commentManager: IDynamicService<ICommentManager>;
    /**
     * 批注个数
     */
    count: number;
    /**
     * 元素id
     */
    elementId?: string | null;
    /**
     * 当前批注组是否被激活
     */
    isActive?: boolean;
    /**
     * 批注角标位置
     */
    anchorPosition?: ICTPoint2DDelta;
    /**
     * 页面缩放比例
     */
    scale?: number;
    /**
     * 元素大小，主要用来限制批注角标不会超出元素大小
     */
    elementSize?: {
        width: number;
        height: number;
    }
    /**
     * 批注角标包括两种：
     * 1. 左侧缩略图角标，支持hover
     * 2. 编辑区交表，支持hover和active
     */
    isLeftCell?: boolean;
}

const CommentAnchor: React.FC<ICommentAnchorProps> = (props: ICommentAnchorProps) => {
    const { elementId, commentManager } = props;
    const [isHover, setIsHover] = useState(false);

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        commentManager?.promise().then((commentManager) => {
            if (props.isLeftCell) {
                commentManager.setCommentShowAction(CommentShowAction.LEFT_SLIDE_COMMENT_ANCHOR);
            } else {
                commentManager.setCommentShowAction(CommentShowAction.EDIT_SLIDE_COMMENT_ANCHOR);
                if (elementId) {
                    commentManager.setActiveGroupIdForElement(elementId);
                }
            }
        });
        report(TDWActionType.MARK_CLK, EventCodeType.DOC_PPT_COMMENTS_MARK_CLK);
    };

    const report = (action: string, eventcode: string) => {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.COMMENTS_SLIDEBAR,
            action,
            eventcode,
            ver5: props.isLeftCell ? 0 : 1,
        });
    };

    /**
     * 标注图标不能超过元素大小范围。如果标注图标高度>元素高度，则图标对齐底边；如果标注图标宽度>元素宽度，则图标对齐右边
     * @returns
     */
    const getStyle = (): CSSProperties => {
        // 表明是左侧缩略图的场景，使用默认样式
        if (props.isLeftCell) return {};

        // 当anchorPosition不存在时，默认位置为右上角。
        if (!props.anchorPosition) {
            const scale = props.scale ?? 1;
            // 标注图标高度>元素高度，则图标对齐底边
            if (typeof props.elementSize?.height === 'number' && props.elementSize.height * scale < ANCHOR_HEIGHT) {
                return { bottom: 0, right: 0, left: 'auto', top: 'auto' };
            }
            return { top: 0, right: 0, left: 'auto' };
        }

        // 当anchorPosition存在时
        const style: CSSProperties = {};
        style.left = calculateScaledPosition(ANCHOR_WIDTH, props.anchorPosition.x, props.elementSize?.width);
        // 标注图标高度>元素高度，则图标对齐右边
        style.right = style.left === 'auto' ? 0 : 'auto';
        style.top = calculateScaledPosition(ANCHOR_HEIGHT, props.anchorPosition.y, props.elementSize?.height);
        // 标注图标高度>元素高度，则图标对齐底边
        style.bottom = style.top === 'auto' ? 0 : 'auto';
        return style;
    };

    const calculateScaledPosition = (anchorSize: number, position?: number | null, elementSize?: number | null): number | 'auto' => {
        const scale = props.scale ?? 1;
        if (typeof position !== 'number' || typeof elementSize !== 'number') return 'auto';
        const scaledPosition = (position * scale) - anchorSize;
        return scaledPosition < 0 ? 'auto' : Math.max(0, Math.min(elementSize * scale, scaledPosition));
    };

    const handleMouseEnter = () => {
        setIsHover(true);
        if (elementId) {
            commentManager.promise().then((commentManager) => {
                if (!commentManager.isCommentDrawerShown) return;
                commentManager.setHoverElementId(elementId);
            });
        }
    };

    const handleMouseLeave = () => {
        setIsHover(false);
        if (elementId) {
            commentManager.promise().then((commentManager) => {
                if (!commentManager.isCommentDrawerShown) return;
                commentManager.setHoverElementId(undefined);
            });
        }
    };

    return <div
        className={Style['comment-anchor-container']}
        style={getStyle()}
        onClick={handleClick}
        onDoubleClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
    >
        {
            (props.isActive || isHover)
                ? <img src={props.count >= 100 ? ActiveCommentLargerThan100Icon : ActiveCommentLessThan100Icon} />
                : <img src={props.count >= 100 ? CommentLargerThan100Icon : CommentLessThan100Icon} />
        }
        <div
            className={Style['comment-anchor-count']}
            style={{ marginTop: props.count >= 100 ? -26 : -24 }}
        >
            {props.count}
        </div>
    </div>;
};

export default CommentAnchor;
